import { useCallback, useRef } from "react";
import { CreatePageState, useCreatePageStore } from "@/stores/createPageStore";
import { logger } from "@/utils/logger";
import { trackVariationInteraction } from "@/utils/analytics";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import { ActionHookContext } from "./types";

export function useVariationActions(
  context: ActionHookContext,
  state: CreatePageState
) {
  const { store, stateRef } = context;

  // Track when variations were first displayed for time-to-select calculation
  const variationsShownTimeRef = useRef<{
    front: number | null;
    back: number | null;
  }>({
    front: null,
    back: null,
  });

  const selectFrontVariation = useCallback(
    async (index: number) => {
      // Prevent double-clicks (debouncing)
      if (state.generation.images.isSelectingVariation) return;

      // Store previous index for error recovery
      const previousIndex = state.generation.images.selected.frontIndex;

      // Set selecting state to prevent double-clicks
      store.setSelectingVariation(true);

      // Update selection state immediately for better UX
      store.selectFrontVariation(index);

      // Track variation selection for analytics
      const currentState = stateRef.current;
      const variationCount =
        currentState.generation.images.variations.front.length;
      const isFirstSelection = previousIndex === null;
      const timeToSelect = variationsShownTimeRef.current.front
        ? Date.now() - variationsShownTimeRef.current.front
        : undefined;

      trackVariationInteraction({
        action: isFirstSelection ? "selected" : "changed",
        variation_count: variationCount,
        selected_index: index,
        time_to_select_ms: timeToSelect,
        image_type: "front",
      });

      try {
        // Use stateRef.current to get the latest state (avoids stale closure issues)
        const currentState = stateRef.current;

        if (
          currentState.generation.images.variations.front.length > 0 &&
          currentState.generation.images.variations.front[index]
        ) {
          // Get ticket ID for this variation (tickets are created server-side by generate-image API)
          let variationTicketId =
            currentState.ticket.variationTicketIds?.[index];

          // Retry logic: if ticket not found immediately, wait briefly and retry
          // This handles race conditions where tickets are still being created asynchronously
          if (!variationTicketId) {
            logger.debug(
              `Ticket ID not found immediately for variation ${index}, retrying`,
              {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: index,
                  variationTicketIdsState:
                    currentState.ticket.variationTicketIds,
                },
              }
            );

            const maxRetries = 3;
            const retryDelay = 100; // 100ms delay between retries

            for (let retry = 0; retry < maxRetries; retry++) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              // Get fresh state after delay
              const freshState = stateRef.current;
              variationTicketId = freshState.ticket.variationTicketIds?.[index];

              if (variationTicketId) {
                logger.info(
                  `Found ticket for variation ${index} after retry ${retry + 1}`,
                  {
                    component: "CreatePageActions",
                    metadata: {
                      variationIndex: index,
                      ticketId: variationTicketId,
                      retryAttempt: retry + 1,
                    },
                  }
                );
                break;
              }
            }
          }

          if (variationTicketId) {
            // Ticket exists for this variation - switch to it
            store.setTicketId(variationTicketId);
            logger.info("Switched to ticket for selected front variation", {
              component: "CreatePageActions",
              metadata: {
                ticketId: variationTicketId,
                variationIndex: index,
              },
            });
          } else {
            // No ticket found - this should not happen as tickets are created by the API
            logger.error(
              `No ticket found for variation ${index}. Tickets should be created by generate-image API.`,
              {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: index,
                  totalVariations:
                    currentState.generation.images.variations.front.length || 0,
                  variationTicketIdsState:
                    currentState.ticket.variationTicketIds,
                  currentTicketId: currentState.ticket.id,
                },
              }
            );
            throw new Error(
              `No ticket found for variation ${index}. Please refresh the page.`
            );
          }
        }
      } catch {
        // Revert selection on error
        const currentState = stateRef.current;
        if (
          currentState.generation.images.variations.front.length > 0 &&
          currentState.generation.images.variations.front[index]
        ) {
          if (previousIndex !== null) {
            store.selectFrontVariation(previousIndex);
          } else {
            store.selectFrontVariation(0);
          }
        }
        store.setError("We couldn't save your selection. Please try again.");
      } finally {
        // Always clear selecting state
        store.setSelectingVariation(false);
      }
    },
    [
      state.generation.images.isSelectingVariation,
      state.generation.images.selected.frontIndex,
      store,
      stateRef,
    ]
  );

  const selectBackVariation = useCallback(
    async (index: number) => {
      // Prevent double-clicks (debouncing)
      if (state.generation.images.isSelectingVariation) return;

      // Store previous index for error recovery
      const previousIndex = state.generation.images.selected.backIndex;

      // Set selecting state to prevent double-clicks
      store.setSelectingVariation(true);

      // Update selection state immediately for better UX
      store.selectBackVariation(index);

      // Track variation selection for analytics
      const currentState = stateRef.current;
      const variationCount =
        currentState.generation.images.variations.back.length;
      const isFirstSelection = previousIndex === null;
      const timeToSelect = variationsShownTimeRef.current.back
        ? Date.now() - variationsShownTimeRef.current.back
        : undefined;

      trackVariationInteraction({
        action: isFirstSelection ? "selected" : "changed",
        variation_count: variationCount,
        selected_index: index,
        time_to_select_ms: timeToSelect,
        image_type: "back",
      });

      try {
        const currentState = stateRef.current;
        // While Back is retained only for reference, its old variation tickets
        // no longer describe the current Front. Keep the visual selection, but
        // leave the current Front ticket untouched until Back is refreshed.
        if (
          projectSavedBackStaleness(
            useCreatePageStore.getState().generation.images
          ).stale
        )
          return;
        if (
          currentState.generation.images.variations.back.length > 0 &&
          currentState.generation.images.variations.back[index]
        ) {
          // Get ticket ID for this variation (tickets are created server-side by generate-image API)
          let variationTicketId =
            currentState.ticket.variationTicketIds?.[index];

          // Retry logic: if ticket not found immediately, wait briefly and retry
          // This handles race conditions where tickets are still being created asynchronously
          if (!variationTicketId) {
            logger.debug(
              `Ticket ID not found immediately for back variation ${index}, retrying`,
              {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: index,
                  variationTicketIdsState:
                    currentState.ticket.variationTicketIds,
                },
              }
            );

            const maxRetries = 3;
            const retryDelay = 100; // 100ms delay between retries

            for (let retry = 0; retry < maxRetries; retry++) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              // Get fresh state after delay
              const freshState = stateRef.current;
              variationTicketId = freshState.ticket.variationTicketIds?.[index];

              if (variationTicketId) {
                logger.info(
                  `Found ticket for back variation ${index} after retry ${retry + 1}`,
                  {
                    component: "CreatePageActions",
                    metadata: {
                      variationIndex: index,
                      ticketId: variationTicketId,
                      retryAttempt: retry + 1,
                    },
                  }
                );
                break;
              }
            }
          }

          if (variationTicketId) {
            // Ticket exists for this variation - switch to it
            store.setTicketId(variationTicketId);
            logger.info("Switched to ticket for selected back variation", {
              component: "CreatePageActions",
              metadata: {
                ticketId: variationTicketId,
                variationIndex: index,
              },
            });
          } else {
            // No ticket found - this should not happen as tickets are created by the API
            logger.error(
              `No ticket found for back variation ${index}. Tickets should be created by generate-image API.`,
              {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: index,
                  totalVariations:
                    currentState.generation.images.variations.back.length || 0,
                  variationTicketIdsState:
                    currentState.ticket.variationTicketIds,
                  currentTicketId: currentState.ticket.id,
                },
              }
            );
            throw new Error(
              `No ticket found for back variation ${index}. Please refresh the page.`
            );
          }
        }
      } catch {
        // Revert selection on error
        const currentState = stateRef.current;
        if (
          currentState.generation.images.variations.back.length > 0 &&
          currentState.generation.images.variations.back[index]
        ) {
          if (previousIndex !== null) {
            store.selectBackVariation(previousIndex);
          } else {
            store.selectBackVariation(0);
          }
        }
        store.setError("We couldn't save your selection. Please try again.");
      } finally {
        // Always clear selecting state
        store.setSelectingVariation(false);
      }
    },
    [
      state.generation.images.isSelectingVariation,
      state.generation.images.selected.backIndex,
      store,
      stateRef,
    ]
  );

  return {
    selectFrontVariation,
    selectBackVariation,
  };
}
